import { useAppSelector } from "store/store-hooks";
import { ProtectRouteProps } from "./protect-route.type";
import { Navigate, useLocation } from "react-router-dom";
import { ROUTE_PATH } from "router/router-constant/router.constant";
import { useReadUserSubscriptionQuery } from "api/endpoints/subscription";
import React from "react";

export const ProtectRoute = ({
  children,
  protectUnAuthorized,
  allowedRoles,
  requireNoSubscription,
  requireSubscription,
  fallbackPath,
}: ProtectRouteProps) => {
  const location = useLocation();
  const { token: savedAccessToken, role: userRole } = useAppSelector(
    ({ authReducer: { token, role } }) => ({
      token,
      role,
    })
  );

  // Always call the hook, but conditionally use the data
  const shouldCheckSubscription = requireNoSubscription || requireSubscription;
  const { data: userSubscription } = useReadUserSubscriptionQuery(undefined, {
    skip: !shouldCheckSubscription,
  });

  const isAuth = savedAccessToken;
  const hasSubscription = shouldCheckSubscription && userSubscription && userSubscription !== null;
  
  // Check if this is a renewal operation
  const isRenewal = new URLSearchParams(location.search).get('renew') === 'true';

  // Handle routes that should only be accessible to unauthenticated users (like login/register)
  if (protectUnAuthorized) {
    if (isAuth) {
      // User is authenticated but trying to access login/register page, redirect to main page
      return <Navigate to="/" replace />;
    }
    return children;
  }

  // If not authenticated and this route requires authentication, redirect to login
  if (!isAuth) {
    return <Navigate to={ROUTE_PATH.auth.login.path} replace />;
  }

  // Check role-based access
  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    const redirectPath = fallbackPath || getDefaultRedirectPath(userRole);
    return <Navigate to={redirectPath} replace />;
  }

  // Check subscription-based access
  if (requireNoSubscription && hasSubscription && !isRenewal) {
    const redirectPath = fallbackPath || ROUTE_PATH.subscriptions.mySubscription.path;
    return <Navigate to={redirectPath} replace />;
  }

  if (requireSubscription && !hasSubscription) {
    const redirectPath = fallbackPath || ROUTE_PATH.subscriptions.purchase.path;
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// Helper function to get default redirect path based on user role
const getDefaultRedirectPath = (role: "admin" | "user"): string => {
  switch (role) {
    case "admin":
      return ROUTE_PATH.users.list.path;
    case "user":
      return ROUTE_PATH.subscriptions.mySubscription.path;
    default:
      return ROUTE_PATH.auth.login.path;
  }
};
